import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

interface FormFieldConfig {
  name: string;
  webName: string;
  type: string;
  required: boolean;
  options?: any[]; // Para campos de tipo select
}

@Component({
  selector: 'app-ticket-holder-data',
  templateUrl: './ticket-holder-data.component.html',
  styleUrls: ['./ticket-holder-data.component.scss'],
  standalone: false
})
export class TicketHolderDataComponent implements OnInit {
  myForm: FormGroup;
  ticketName: string;
  quantity: number;
  ticketIndex: number;
  totalQuantity: number;
  formFields: FormFieldConfig[] = [];
  
  // Labels
  titleLabel: string;
  cancelLabel: string;
  confirmLabel: string;
  requiredFieldLabel: string;
  invalidEmailLabel: string;
  invalidDateLabel: string;
  quantityLabel: string;
  ticketIndexLabel: string;
  isSpanish: boolean;

  constructor(
    public dialogRef: MatDialogRef<TicketHolderDataComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder
  ) {
    this.ticketName = data.ticketName;
    this.quantity = data.quantity || 1;
    this.ticketIndex = data.ticketIndex || 1;
    this.totalQuantity = data.totalQuantity || 1;
    // Asegurar que formFields sea siempre un array
    // formData puede venir como string JSON o como array
    if (Array.isArray(data.formData)) {
      this.formFields = data.formData;
    } else if (typeof data.formData === 'string') {
      try {
        this.formFields = JSON.parse(data.formData);
        // Verificar que el parseo resultó en un array
        if (!Array.isArray(this.formFields)) {
          this.formFields = [];
        }
      } catch (e) {
        console.error('Error parsing formData JSON:', e);
        this.formFields = [];
      }
    } else {
      this.formFields = [];
    }
    this.isSpanish = localStorage.getItem('lang') === 'es';
    this.titleLabel = this.isSpanish 
      ? 'Datos del titular' 
      : 'Holder information';
    this.cancelLabel = this.isSpanish ? 'Cancelar' : 'Cancel';
    this.confirmLabel = this.isSpanish ? 'Confirmar' : 'Confirm';
    this.requiredFieldLabel = this.isSpanish 
      ? 'Este campo es obligatorio' 
      : 'This field is required';
    this.invalidEmailLabel = this.isSpanish 
      ? 'Email no válido' 
      : 'Invalid email';
    this.invalidDateLabel = this.isSpanish 
      ? 'Fecha no válida' 
      : 'Invalid date';
    this.quantityLabel = this.isSpanish ? 'Cantidad: ' : 'Quantity: ';
    // Label para mostrar el índice de la entrada actual
    if (this.totalQuantity > 1) {
      this.ticketIndexLabel = this.isSpanish 
        ? `${this.ticketIndex} de ${this.totalQuantity}` 
        : `${this.ticketIndex} of ${this.totalQuantity}`;
    } else {
      this.ticketIndexLabel = '';
    }

    // Crear formulario dinámicamente basado en formFields
    this.createDynamicForm();
  }

  ngOnInit(): void {
  }

  /**
   * Crea el formulario dinámicamente basado en formFields
   */
  createDynamicForm(): void {
    const formControls: { [key: string]: FormControl } = {};
    
    // Asegurar que formFields sea un array antes de iterar
    if (!Array.isArray(this.formFields)) {
      this.formFields = [];
    }
    
    this.formFields.forEach(field => {
      const validators = [];
      
      if (field.required) {
        validators.push(Validators.required);
      }
      
      // Agregar validadores específicos según el tipo
      if (field.type === 'email') {
        validators.push(Validators.email);
      }
      
      formControls[field.name] = new FormControl(
        field.type === 'date' ? null : '', 
        validators.length > 0 ? validators : null
      );
    });
    
    this.myForm = this.formBuilder.group(formControls);
  }

  /**
   * Obtiene el tipo de input HTML basado en el tipo del campo
   */
  getInputType(fieldType: string): string {
    switch (fieldType) {
      case 'email':
        return 'email';
      case 'date':
        return 'date';
      case 'number':
        return 'number';
      case 'tel':
        return 'tel';
      default:
        return 'text';
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    if (this.myForm.valid) {
      this.dialogRef.close(this.myForm.value);
    } else {
      // Marcar todos los campos como touched para mostrar errores
      Object.keys(this.myForm.controls).forEach(key => {
        this.myForm.get(key)?.markAsTouched();
      });
    }
  }
}

